import { isSuccess, type Result } from '@carvana/merch-result';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';
import { httpWithAuth } from '~/clients';
import { FinancingApiClientId } from '~/constants/common/ApiClientId';
import { getFinancingTerms } from '~/constants/endpoints';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { CreditApplication, PricingAggregates } from '~/contracts/flp';
import reportToggleComplete, {
  TOGGLE_TYPE
} from '~/utilities/flp/reportToggleComplete';
import showApiError from '~/utilities/flp/showApiError';
import { logError } from '~/utilities/loggers';
import { navPerformanceNow } from '~/utilities/navigationTiming';
import toError from '~/utilities/toError';

export default function useToggleTradeIn() {
  const eventCorrelationId = useEventCorrelationId();
  const { setIsLoading, setRefreshedData } = useFinancingContext();

  return useCallback(
    async (applyTradeIn: boolean) => {
      const toggleStartTime = navPerformanceNow();
      setIsLoading(true);
      datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_TRADEIN_API_CALL);
      try {
        const response = await httpWithAuth.post<
          Result<{
            creditApplication: Result<CreditApplication | null>;
            pricingAggregates?: Result<PricingAggregates>;
          }>
        >(
          getFinancingTerms(applyTradeIn, {
            currentPageUrl: window.location.href
          }),
          {},
          {
            headers: {
              'x-client-id': FinancingApiClientId,
              'x-correlation-id': eventCorrelationId
            }
          }
        );

        if (!isSuccess(response.data)) {
          datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_TRADEIN_API_ERROR, {
            error: response.data.errorMessage
          });

          throw new Error(response.data.errorMessage);
        }

        const { creditApplication, pricingAggregates } = response.data.content;

        if (
          isSuccess(creditApplication) &&
          creditApplication.content &&
          isSuccess(pricingAggregates)
        ) {
          setRefreshedData(
            'Toggle Trade-In',
            applyTradeIn,
            creditApplication.content,
            pricingAggregates.content
          );

          reportToggleComplete(toggleStartTime, TOGGLE_TYPE.TRADEIN);
        }
      } catch (err) {
        datadogRum.addAction(DATADOG_EVENTS.API.TOGGLE_TRADEIN_API_ERROR, {
          error: err
        });
        logError(err, { location: 'Toggle Trade-In' });

        showApiError();

        // throw the error again so the consumer can handle it
        throw toError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [eventCorrelationId, setIsLoading, setRefreshedData]
  );
}
